import { useSelector } from '@xstate/store/react';
import React from 'react';

import {
  Button,
  Icon,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Stack,
  Text,
} from '@eluve/components';

import { useSegmentAudioContext } from './SegmentAudioContextStore';
import { useSegmentSpeakerControls } from './useSegmentSpeakerControls';

export interface SegmentSpeakerControlsProps {
  tenantId: string;
  segmentId: string;
  initialSpeakerNames?: string[];
}

export const SegmentSpeakerControls: React.FC<SegmentSpeakerControlsProps> = ({
  tenantId,
  segmentId,
  initialSpeakerNames,
}) => {
  const { store } = useSegmentAudioContext();
  const selectedUtteranceIds = useSelector(
    store,
    ({ context: { selectedUtteranceIds } }) => selectedUtteranceIds,
  );

  const {
    onUpdateSpeakerForUtterances,
    onRenameSpeaker,
    isSelectSpeakerOpen,
    toggleIsSelectSpeakerOpen,
    newSpeakerName,
    setNewSpeakerName,
    speakerNames,
    setSpeakerNames,
    renameSpeakerTo,
    setRenameSpeakerTo,
    isNewSpeakerOpen,
    toggleIsNewSpeakerOpen,
  } = useSegmentSpeakerControls({ tenantId, segmentId, initialSpeakerNames });

  return (
    <Stack className="rounded-lg border p-2" gap={2}>
      <Text size="md">Speakers</Text>
      <Stack gap={1} wrap direction="horizontal">
        {[...speakerNames].map((speaker) => (
          <Popover key={speaker}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" key={speaker}>
                {speaker}
                <Icon name="Pencil" size="auto" />
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <Stack gap={2} direction="horizontal">
                <Input
                  placeholder="New Speaker Name"
                  onChange={(e) => setRenameSpeakerTo(e.target.value)}
                  value={renameSpeakerTo}
                />
                <Button
                  size="sm"
                  onClick={() => onRenameSpeaker(speaker)}
                  disabled={
                    renameSpeakerTo === '' || renameSpeakerTo === speaker
                  }
                >
                  Rename
                </Button>
              </Stack>
            </PopoverContent>
          </Popover>
        ))}
        <Popover open={isNewSpeakerOpen} onOpenChange={toggleIsNewSpeakerOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <Icon name="Plus" size="auto" />
              New
            </Button>
          </PopoverTrigger>
          <PopoverContent side="right" align="start">
            <Stack gap={2} direction="horizontal">
              <Input
                placeholder="Speaker Name"
                onChange={(e) => setNewSpeakerName(e.target.value)}
                value={newSpeakerName}
              />
              <Button
                size="sm"
                onClick={() => {
                  setSpeakerNames((prev) => new Set([...prev, newSpeakerName]));
                  setNewSpeakerName('');
                }}
                disabled={Boolean(
                  !newSpeakerName || speakerNames.has(newSpeakerName),
                )}
              >
                Add
              </Button>
            </Stack>
          </PopoverContent>
        </Popover>
      </Stack>
      <Separator className="my-1" />
      <Stack gap={1} direction="horizontal">
        {[
          ...speakerNames,
          ...['Doctor', 'Nurse'].filter((s) => !speakerNames.has(s)),
        ].map((speaker) => (
          <Button
            variant="outline"
            size="sm"
            key={speaker}
            onClick={() => onUpdateSpeakerForUtterances(speaker)}
            disabled={selectedUtteranceIds.length === 0}
          >
            <Icon name="User" size="auto" />
            {`Change to ${speaker}`}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
};
